import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { Toaster } from 'app/shared/lib/toaster';
import { I18N } from 'aurelia-i18n';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { EventAggregator } from 'aurelia-event-aggregator';
import { MenuRefreshEvent } from 'app/shared/lib/events';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { localSort } from 'app/shared/lib/sorting';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { deepClone } from 'app/shared/lib/utilities';
import { EmailNotificationsRecipients } from 'app/core/components/email-notifications/email-notifications-recipients'
import { EmailNotificationsSelected } from 'app/core/components/email-notifications/email-notifications-selected';
import * as UM from 'app/um/models/essette-api';

interface CustomAuthQueue extends UM.AuthQueue {
    // Queue Status Value For Grid Column Sorting
    queueStatusValue?: string | null;

    // Queue Status Display For Grid Column Display
    queueStatusDisplay?: string | null;

    // Queue Active Status Tooltip status list For Grid Column
    queueActiveStatusToolTipDesc?: string | null;

    // Queue Inactive Status Tooltip status list For Grid Column
    queueInactiveStatusToolTipDesc?: string | null;

    // For when there are no active statuses but there are inactive statuses
    noActiveStatuses?: boolean | null;
}

@autoinject()
export class ManageAuthQueuesPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected validateForm = validate;
    protected queueFormIsValid: boolean = false;
    protected emailsFormIsValid: boolean = false;
    protected authQueues: ReadonlyArray<CustomAuthQueue> | null = null;
    protected authStatuses: ReadonlyArray<UM.AuthStatus> = [];

    private apiSorting: Sorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    private localSortProperties: string[] = ['queueStatusValue'];
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;
    protected authQueueDetails: CustomAuthQueue | null = null;

    // Permission Access
    protected hasEmailNotificationAccess: boolean = false;

    ////****************** Element References ***************************************////
    private $queueModal!: IPopup;
    protected $queueForm!: IForm;
    private $emailsModal!: IPopup;
    protected $emailsForm!: IForm;
    protected $emailNotificationsRecipients!: EmailNotificationsRecipients;
    protected $emailNotificationsSelected!: EmailNotificationsSelected;
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected async activate(): Promise<void> {
        this.reload();

        await this.checkPermissions();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadAuthStatuses();
        await this.loadAuthQueues();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const authQueues: CustomAuthQueue[] = <CustomAuthQueue[]>this.authQueues;
            if(direction === null) {
                this.localSorting.remove(property);
            } else {
                this.localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(authQueues, localSorting);
            if(this.localSorting.entries.length == 0) {
                this.reload();
            };
        }
        // Else If API Sort
        else {
            const { apiSorting } = this;
            if(direction === null) {
                apiSorting.remove(property);
            } else {
                apiSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();

        this.hasEmailNotificationAccess = this.accountContext.hasRouteAccess('um.admin.auth-queues.email-notifications');
    }

    protected refreshMainMenu(): void {
        this.events.publish(new MenuRefreshEvent);
        this.accountContext.clear();
        this.checkPermissions();
    }

    protected addQueue(): void {
        this.$queueModal.open();
        this.authQueueDetails = null;
    }

    //#region Edit Auth Queues Modal
    protected editAuthQueue(authQueue: CustomAuthQueue) {
        if(!authQueue || !this.$queueModal) {
            return;
        }
        this.authQueueDetails = deepClone(authQueue);
        this.$queueModal.open();
    }

    protected closeQueueModal(): void {
        if(this.$queueForm.reset) {
            this.$queueForm.reset();
        }
        this.$queueModal.close();
    }

    protected handleQueueFormSuccess({ detail }: FormSuccessCustomEvent): void {
        this.$queueModal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        this.refreshMainMenu();
        this.reload();
    }
    //#endregion Edit Auth Queues Modal

    //#region Edit Email Recipients Modal
    protected async handleEmailsFormSuccess(): Promise<void> {
        // reload information in both email notifications components
        if(this.$emailNotificationsRecipients) await this.$emailNotificationsRecipients.reload();
        if(this.$emailNotificationsSelected) {
            this.$emailNotificationsSelected.reset(); // reset selections
            await this.$emailNotificationsSelected.reloadInformation(true); // reload users as well
        }
        await this.reload();
    }

    protected editEmailNotifications(authQueue: CustomAuthQueue) {
        if(!authQueue || !this.$emailsModal) return;
        this.authQueueDetails = deepClone(authQueue);
        this.$emailsModal.open();
    }

    protected async addRecipients() {
        await this.$emailsForm.submit();
    }

    //#endregion Edit Email Recipients Modal

    // Set Auth Status Display
    private setAuthQueueStatus(authQueues: CustomAuthQueue[]): void {
        authQueues?.forEach(queue => {
            if(queue.flag?.allowAnyStatus) {
                queue.queueStatusDisplay = queue.queueStatusValue = this.i18n.tr('ui:labels.any', { what: this.i18n.tr('um:auth.status.$') });
                return;
            }

            if(queue?.statuses && queue.statuses.length > 0) {
                const activeStatuses = queue.statuses.filter(status => status.isActive);
                const inactiveStatuses = queue.statuses.filter(status => !status.isActive);

                // if no active statuses and no inactive statuses, display nothing at all
                if(activeStatuses.length + inactiveStatuses.length === 0) {
                    queue.queueStatusDisplay = queue.queueStatusValue = '';
                    return;
                }

                // if only one active status, display only that status and no tooltip
                if(activeStatuses.length === 1) {
                    queue.queueStatusValue = queue.statuses[0].description;
                    queue.queueStatusDisplay = queue.statuses[0].$display;
                    return;
                }

                // if more than one active status, display "n Statuses"
                if(activeStatuses.length > 0) {
                    queue.queueStatusDisplay = queue.queueStatusValue = `${activeStatuses.length} ` + this.i18n.tr('um:auth.status.$', { count: 2 });
                }

                // if no active statuses and more than zero inactive statuses, display "None"
                if(activeStatuses.length === 0 && inactiveStatuses.length > 0) {
                    queue.queueStatusDisplay = queue.queueStatusValue = this.i18n.tr('ui:labels.none');
                    queue.noActiveStatuses = true;
                }

                // generate the tooltip with both active and inactive statuses
                const activeStatusList = activeStatuses.flatMap(s => s.description ?? '')
                    .sort((a, b) => a.localeCompare(b));
                const inactiveStatusList = inactiveStatuses.flatMap(s => s.description ?? '')
                    .sort((a, b) => a.localeCompare(b));

                if(!activeStatusList.length) {
                    queue.queueActiveStatusToolTipDesc = null;
                } else {
                    let activeListDisplay = '<ul>';
                    activeStatusList.forEach(s => activeListDisplay += '<li>' + s + '</li>');
                    activeListDisplay += '</ul>';
                    queue.queueActiveStatusToolTipDesc = activeListDisplay;
                }

                if(!inactiveStatusList.length) {
                    queue.queueInactiveStatusToolTipDesc = null;
                } else {
                    let inactiveListDisplay = '<ul>';
                    inactiveStatusList.forEach(s => inactiveListDisplay += '<li>' + s + '</li>');
                    inactiveListDisplay += '</ul>';
                    queue.queueInactiveStatusToolTipDesc = inactiveListDisplay;
                }
            }
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAuthStatuses(): Promise<void> {
        const includes: string[] = [];

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            includeInactive: false
        };

        try {
            this.state.beginLoad();

            const response = await this.authService.loadAuthStatuses(options, includes);
            this.authStatuses = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAuthQueues(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.apiSorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const includeInactiveStatuses = true;
            const response = await this.authService.loadQueues(options, [], includeInactiveStatuses);
            const loadingAuthQueues = <CustomAuthQueue[]>response.results;
            // Set Auth Queue Statuses
            this.setAuthQueueStatus(loadingAuthQueues);

            this.authQueues = loadingAuthQueues;
            this.resultCount = response.count;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async deactivateQueue(authQueue: CustomAuthQueue): Promise<void> {
        if(!authQueue.id) return;

        const result = await confirmation.showDeactivate('um:queue.$long', 1, false);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        if(!(result.dialogResponse === confirmation.options.OK)) {
            return;
        }

        try {
            this.state.beginSave();
            const response = await this.authService.activateQueue(authQueue.id, false);

            if(response.isSuccessful) {
                this.refreshMainMenu();
                this.reload();
            }
            this.toaster.standard.activate('um:queue.$long', false, response.isSuccessful);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async reactivateQueue(authQueue: CustomAuthQueue): Promise<void> {
        if(!authQueue.id) return;

        try {
            this.state.beginSave();
            const response = await this.authService.activateQueue(authQueue.id, true);

            if(response.isSuccessful) {
                this.refreshMainMenu();
                this.reload();
            }
            this.toaster.standard.activate('um:queue.$long', true, response.isSuccessful);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}
